import { SuggesterResultsContextProvider } from '@cfra-nextgen-frontend/shared/src/components/SmartSearch/SuggesterResultsContext';
import {
    SharedTopNavigation,
    TopNavItem,
} from '@cfra-nextgen-frontend/shared/src/components/TopNavigation/SharedTopNavigation';
import { clearAllLocalPreferences } from '@cfra-nextgen-frontend/shared/src/components/UserPreferences/utils/local';
import { UserEntitlementsResponseData } from '@cfra-nextgen-frontend/shared/src/types/userEntitlements';
import { ApplicationType } from '@cfra-nextgen-frontend/shared/src/utils/enums';
import CFRAMarketScopeLogo from 'assets/images/MarketScopeAdvisor.svg';
import { MarketScopeLogo } from 'components/Logo/MarketScopeLogo';
import { commonCustomBreakpointsTheme } from 'components/themes/customBreakpointsTheme';
import React, { useCallback, useMemo, useState } from 'react';

export function TopNavigation({ userEntitlements }: { userEntitlements?: UserEntitlementsResponseData }) {
    const [researchSuggesterResultsNumber, setResearchSuggesterResultsNumber] = useState<number | undefined | null>(
        undefined,
    );
    const [companySuggesterResultsNumber, setCompanySuggesterResultsNumber] = useState<number | undefined | null>(
        undefined,
    );

    return (
        <SuggesterResultsContextProvider
            researchSuggesterResultsNumber={researchSuggesterResultsNumber}
            companySuggesterResultsNumber={companySuggesterResultsNumber}>
            <TopNavigationInner
                userEntitlements={userEntitlements}
                researchSuggesterResultsNumber={researchSuggesterResultsNumber}
                setResearchSuggesterResultsNumber={setResearchSuggesterResultsNumber}
                companySuggesterResultsNumber={companySuggesterResultsNumber}
                setCompanySuggesterResultsNumber={setCompanySuggesterResultsNumber}
            />
        </SuggesterResultsContextProvider>
    );
}

export function TopNavigationInner({
    userEntitlements,
    researchSuggesterResultsNumber,
    setResearchSuggesterResultsNumber,
    companySuggesterResultsNumber,
    setCompanySuggesterResultsNumber,
}: {
    userEntitlements?: UserEntitlementsResponseData;
    researchSuggesterResultsNumber: number | undefined | null;
    setResearchSuggesterResultsNumber: (number: number | undefined | null) => void;
    companySuggesterResultsNumber: number | undefined | null;
    setCompanySuggesterResultsNumber: (number: number | undefined | null) => void;
}) {
    const logoSrc = useMemo(() => {
        return CFRAMarketScopeLogo;
    }, []);

    const resetPasswordLogoJsx = useMemo(() => {
        return (
            <MarketScopeLogo
                useMobileVariant={false}
                showCfraLogo={false}
                heading={'MARKETSCOPE'}
                subHeading={'Advisor'}
                headingSx={{
                    lineHeight: '1',
                    marginBottom: '0px',
                    marginTop: '0px',
                    color: '#002b5a',
                    fontSize: '36px',
                }}
                subHeadingSx={{
                    lineHeight: '1',
                    marginBottom: '0px',
                    marginTop: '0px',
                    color: '#002b5a',
                    fontFamily: 'GraphikLight',
                    fontSize: '36px',
                }}
            />
        );
    }, []);

    const topNavItems = useMemo<Record<string, TopNavItem>>(() => {
        return {
            Home: {
                name: 'Home',
                href: '/',
                type: 'link',
            },
            ResearchHub: {
                name: 'Research Hub',
                href: '/research-hub',
                type: 'link',
            },
        };
    }, []);

    const TopNavigationWithProps = useCallback(
        (props: { isLoading: boolean }) => {
            return (
                <SharedTopNavigation
                    isLoading={props.isLoading}
                    resetPasswordLogoJsx={resetPasswordLogoJsx}
                    maxWidth={'unset'}
                    topNavItems={topNavItems}
                    logoSrc={logoSrc}
                    breakpointsThemeOptions={commonCustomBreakpointsTheme}
                    enableAiSearch={false}
                    smartSearchProps={{
                        onResearchHubClick: (query) => {},
                        onSymbolClick: (symbol) => {
                            console.log('Symbol clicked:', symbol);
                            // Navigate to symbol page
                        },
                        onResearchClick: (research) => {
                            console.log('Research clicked:', research);
                            // Open research document
                        },
                        onPopularSearchClick: (search) => {
                            console.log('Popular search clicked:', search);
                            // Execute the popular search
                        },
                        getLeftOptions: () => <React.Fragment />,
                        getRightOptions: () => <React.Fragment />,
                        onInputChange: (inputValue) => {
                            if (inputValue) {
                                return;
                            }

                            setCompanySuggesterResultsNumber(undefined);
                            setResearchSuggesterResultsNumber(undefined);
                        },
                    }}
                    onLogout={() => {
                        clearAllLocalPreferences(ApplicationType.WealthPortal);
                    }}
                />
            );
        },
        [
            logoSrc,
            resetPasswordLogoJsx,
            topNavItems,
            setCompanySuggesterResultsNumber,
            setResearchSuggesterResultsNumber,
        ],
    );

    const result = useMemo(() => {
        return <TopNavigationWithProps isLoading={!userEntitlements} />;
    }, [userEntitlements, TopNavigationWithProps]);

    return result;
}
